import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient, skipToken } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router";
import { requiredVariantsFor } from "@atsready/shared/blog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSectionCard } from "@/components/admin/AdminSectionCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ClusterInvariantsPanel } from "@/components/blog/ClusterInvariantsPanel";
import { ConflictDialog } from "@/components/blog/ConflictDialog";
import { LocaleSelect } from "@/components/blog/LocaleSelect";
import { LocaleEditorForm, type LocaleEditorSubmission } from "@/components/blog/LocaleEditorForm";
import { MediaPanel } from "@/components/blog/MediaPanel";
import { RestoreBanner } from "@/components/blog/RestoreBanner";
import { RevisionDiff } from "@/components/blog/RevisionDiff";
import { RevisionList } from "@/components/blog/RevisionList";
import { ViewerFrame } from "@/components/blog/ViewerFrame";
import { MARKETING_BASE_URL } from "@/config/env";
import { useToast } from "@/hooks/use-toast";
import { describeLocaleVersions, isFrameMode, type FrameMode } from "@/lib/blog/frame";
import {
  comparableFields,
  issuesForLocale,
  pairConflictFields,
  partitionFactIssues,
  publishConfirmCopy,
  FACT_DRIFT_GUIDANCE,
  FACT_GAP_GUIDANCE,
  type ConflictFieldPair,
} from "@/lib/blog/editor-model";
import {
  clearDraft,
  pendingLocales,
  putDraft,
  readDraft,
  unsavedWarningCopy,
  type LocaleDraftCache,
} from "@/lib/blog/draft-cache";
import { buildLocaleFormDefaults } from "@/lib/blog/form-defaults";
import { defaultDiffPair, diffFields } from "@/lib/blog/history-model";
import {
  englishNameFor,
  LTR_ISOLATE_ATTRS,
  resolveSelectedLocale,
} from "@/lib/blog/locale-options";
import { classifyRequestFailure, describeFailure } from "@/lib/blog/request-failure";
import type { BlogLocaleFormInput } from "@/lib/schemas/blog-schemas";
import type { MediaTheme, MediaViewport } from "@/lib/blog/media-model";
import {
  classifyWriteConflict,
  getBlogCluster,
  getBlogLocaleHead,
  getBlogMedia,
  getBlogRevision,
  isConflict,
  listBlogRevisions,
  publishBlockers,
  publishBlogCluster,
  restoreBlogRevision,
  saveBlogClusterFacts,
  saveBlogClusterStepMinutes,
  saveBlogLocaleHead,
  uploadBlogMediaVariant,
  type BlogWriteConflict,
} from "@/services/blog";
import type { BlogValidationIssue, FactDefinition } from "@/lib/blog/types";

const MODES = ["view", "edit", "history", "media"] as const;
type Mode = (typeof MODES)[number];

function isMode(value: string | null): value is Mode {
  return (MODES as readonly string[]).includes(value ?? "");
}

const DETAIL_ENDPOINT = "GET /v1/admin/blog/posts/{slug}";

export default function BlogPostDetail() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [conflict, setConflict] = useState<{
    conflict: BlogWriteConflict & { kind: "stale" };
    pairs: ConflictFieldPair[];
    submission: LocaleEditorSubmission;
  } | null>(null);
  const [blockers, setBlockers] = useState<BlogValidationIssue[]>([]);
  const [publishConfirm, setPublishConfirm] = useState(false);
  const [restored, setRestored] = useState<{
    restoredVersion: number;
    sourceVersion: number;
  } | null>(null);
  const [uploadFailure, setUploadFailure] = useState<string | undefined>(undefined);
  const [diffFrom, setDiffFrom] = useState<number | null>(null);

  /**
   * AM-15.2 — typed work survives a locale switch.
   *
   * State rather than a ref: the shell reads it while rendering (to seed a
   * remounted form and to name which copies are still dirty), and a value read
   * during render has to be state or the render is not a function of it.
   */
  const [drafts, setDrafts] = useState<LocaleDraftCache<BlogLocaleFormInput>>({});

  /**
   * Bumped to force the locale form to remount and re-seed from the cache.
   *
   * Only "take theirs" needs it: an ordinary locale switch already remounts
   * through the key, but adopting the other editor's copy has to replace values
   * in a form that is already mounted.
   */
  const [formEpoch, setFormEpoch] = useState(0);

  /**
   * Suppresses exactly one unmount flush.
   *
   * The form being replaced by "take theirs" still holds my values, and its
   * cleanup would write them straight back over the copy I just chose. Touched
   * only inside callbacks, never during render.
   */
  const suppressFlush = useRef(false);

  const clusterQuery = useQuery({
    queryKey: ["admin-blog-cluster", slug],
    queryFn: () => getBlogCluster(slug),
  });

  const cluster = clusterQuery.data?.data;
  const entries = useMemo(() => cluster?.locales ?? [], [cluster]);
  const locale = resolveSelectedLocale(entries, params.get("locale"));
  const entry = entries.find((candidate) => candidate.locale === locale);
  const mode: Mode = isMode(params.get("mode")) ? (params.get("mode") as Mode) : "view";
  const frameMode: FrameMode = isFrameMode(params.get("frame"))
    ? (params.get("frame") as FrameMode)
    : "published";

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next);
  };

  /**
   * Jump straight to the copy a validation issue is about.
   *
   * Locale and mode move together on purpose: an issue that names
   * `locales.de.factValues.trial-days` is asking someone to type German, and
   * landing them on the German *viewer* would make them find the editor tab
   * themselves for every gap in the cluster.
   */
  const openLocaleEditor = (next: string) => {
    const params2 = new URLSearchParams(params);
    params2.set("locale", next);
    params2.set("mode", "edit");
    setParams(params2);
  };

  const copyQuery = useQuery({
    queryKey: ["admin-blog-copy", slug, locale],
    queryFn: () => getBlogLocaleHead(slug, locale),
    enabled: entry?.exists === true,
  });
  const copy = copyQuery.data?.data;

  const revisionsQuery = useQuery({
    queryKey: ["admin-blog-revisions", slug, locale],
    queryFn: () => listBlogRevisions(slug, locale),
    enabled: mode === "history" && entry?.exists === true,
  });
  const revisions = revisionsQuery.data?.data ?? [];

  const mediaQuery = useQuery({
    queryKey: ["admin-blog-media", slug],
    queryFn: () => getBlogMedia(slug),
    enabled: mode === "media",
  });

  const diffPair = useMemo(() => {
    const base = defaultDiffPair({
      currentVersion: copy?.version ?? 0,
      publishedVersion: entry?.publishedVersion ?? null,
    });
    return diffFrom === null ? base : { from: diffFrom, to: base.to };
  }, [copy, entry, diffFrom]);

  // `skipToken` rather than `enabled` plus a fallback version: the query is
  // only meaningful with a revision selected, and a `?? 0` for the case the
  // enabling condition already excludes is a number nothing could ever fetch.
  const diffFromVersion = diffPair.from;
  const diffFromQuery = useQuery({
    queryKey: ["admin-blog-revision", slug, locale, diffFromVersion],
    queryFn:
      mode === "history" && diffFromVersion !== null
        ? () => getBlogRevision(slug, locale, diffFromVersion)
        : skipToken,
  });

  const diffs = useMemo(() => {
    const before = diffFromQuery.data?.data;
    if (before === undefined || copy === undefined) return [];
    return diffFields(comparableFields(before), comparableFields(copy));
  }, [diffFromQuery.data, copy]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin-blog-cluster", slug] });
    await queryClient.invalidateQueries({ queryKey: ["admin-blog-copy", slug, locale] });
    await queryClient.invalidateQueries({ queryKey: ["admin-blog-revisions", slug, locale] });
  };

  const saveMutation = useMutation({
    mutationFn: async ({
      submission,
      baseVersion,
    }: {
      submission: LocaleEditorSubmission;
      baseVersion: number;
    }) => {
      try {
        return await saveBlogLocaleHead(slug, locale, {
          baseVersion,
          pathSlug: submission.values.pathSlug ?? null,
          title: submission.values.title,
          description: submission.values.description,
          excerpt: submission.values.excerpt,
          keywords: submission.values.keywords,
          glossary: submission.values.glossary,
          factValues: submission.factValues,
          body: submission.body,
        });
      } catch (error) {
        if (isConflict(error as { status?: number; message: string })) {
          const classified = await classifyWriteConflict({
            slug,
            locale,
            baseVersion,
            message: (error as Error).message,
          });
          throw Object.assign(new Error(classified.message), { classified, submission });
        }
        throw error;
      }
    },
    onSuccess: async () => {
      setDrafts((current) => clearDraft(current, locale));
      toast({ title: "Saved", description: "This copy now has unpublished changes." });
      await invalidate();
    },
    onError: (
      error: Error & { classified?: BlogWriteConflict; submission?: LocaleEditorSubmission },
    ) => {
      const classified = error.classified;
      if (classified?.kind === "stale" && error.submission !== undefined) {
        setConflict({
          conflict: classified,
          pairs: pairConflictFields({
            mine: {
              title: error.submission.values.title,
              description: error.submission.values.description,
              excerpt: error.submission.values.excerpt,
              pathSlug: error.submission.values.pathSlug ?? null,
              keywords: error.submission.values.keywords,
              body: error.submission.body,
              glossary: error.submission.values.glossary,
              factValues: error.submission.factValues,
            },
            theirs: classified.head,
          }),
          submission: error.submission,
        });
        return;
      }
      toast({ title: "Could not save", description: error.message, variant: "destructive" });
    },
  });

  const clusterMutation = useMutation({
    mutationFn: async (input: {
      facts: FactDefinition[];
      steps: { index: number; minutes: number }[];
      clusterVersion: number;
    }) => {
      await saveBlogClusterFacts(slug, {
        clusterVersion: input.clusterVersion,
        facts: input.facts,
      });
      if (input.steps.length > 0) {
        await saveBlogClusterStepMinutes(slug, { steps: input.steps });
      }
    },
    onSuccess: async () => {
      toast({ title: "Shared values saved" });
      await invalidate();
    },
    onError: (error: Error) => {
      toast({
        title: "Could not save shared values",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => publishBlogCluster(slug),
    onSuccess: async (result) => {
      setBlockers([]);
      setRestored(null);
      // Two outcomes, and conflating them is what made this button misleading.
      // The site is a static export: the rows flipping is not the post going
      // live, and if the rebuild was not dispatched then nothing a reader can
      // see has changed yet — which the old "Published" toast asserted anyway.
      toast(
        result.data.runUrl === null
          ? {
              title: "Published — rebuild not started",
              description:
                `${String(result.data.publishedLocales.length)} locales published, but the ` +
                "marketing rebuild could not be dispatched, so the site is unchanged. " +
                "Run publish-post.yml to retry; republishing changes nothing else.",
              variant: "destructive",
            }
          : {
              title: "Published",
              description: `${String(result.data.publishedLocales.length)} locales published. The site is rebuilding.`,
            },
      );
      await invalidate();
    },
    onError: (error: Error & { status?: number; details?: unknown }) => {
      const found = publishBlockers(error);
      setBlockers(found);
      if (found.length === 0) {
        toast({ title: "Could not publish", description: error.message, variant: "destructive" });
      }
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (version: number) =>
      restoreBlogRevision(slug, locale, version, { baseVersion: copy?.version ?? 0 }),
    onSuccess: async (result, version) => {
      setRestored({ restoredVersion: result.data.version, sourceVersion: version });
      await invalidate();
    },
    onError: (error: Error) => {
      toast({ title: "Could not restore", description: error.message, variant: "destructive" });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (input: {
      target: { screenshotId: string; theme: MediaTheme; viewport: MediaViewport };
      file: File;
      reason: string;
      acceptDimensions: boolean;
    }) =>
      uploadBlogMediaVariant(slug, {
        locale,
        screenshotId: input.target.screenshotId,
        theme: input.target.theme,
        viewport: input.target.viewport,
        file: input.file,
        reason: input.reason,
        acceptDimensions: input.acceptDimensions,
      }),
    onSuccess: async () => {
      setUploadFailure(undefined);
      await queryClient.invalidateQueries({ queryKey: ["admin-blog-media", slug] });
    },
    onError: (error: Error) => {
      setUploadFailure(error.message);
    },
  });

  if (clusterQuery.isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="blog-detail-loading">
        Loading post…
      </p>
    );
  }

  if (clusterQuery.isError || cluster === undefined || entry === undefined) {
    const failure = classifyRequestFailure(clusterQuery.error);
    return (
      <div className="space-y-6">
        <AdminPageHeader title="Blog Post" description={slug} />
        <AdminSectionCard
          title={failure.kind === "forbidden" ? "Access refused by the server" : "Post unavailable"}
        >
          <div
            data-testid={
              failure.kind === "forbidden" ? "blog-detail-forbidden" : "blog-detail-error"
            }
          >
            <p className="max-w-prose text-sm text-muted-foreground">
              {describeFailure(failure, DETAIL_ENDPOINT)}
            </p>
            {failure.kind === "failed" && (
              <Button className="mt-4" onClick={() => void clusterQuery.refetch()}>
                Retry request
              </Button>
            )}
          </div>
        </AdminSectionCard>
      </div>
    );
  }

  const versions = describeLocaleVersions(entry);
  const verdict = cluster.validation;
  const errors = verdict?.errors ?? [];
  const warnings = verdict?.warnings ?? [];
  // AM-18 — a missing rendering and a drifted id are both errors, but they ask
  // different things of different people, so they are read apart from the rest.
  const { gaps: factGaps, drifts: factDrifts, rest: otherErrors } = partitionFactIssues(errors);
  const missingCount = entries.filter((candidate) => !candidate.exists).length;
  const publishReasons = [
    ...(errors.length > 0 ? [`${String(errors.length)} validation errors`] : []),
    ...(missingCount > 0
      ? [`${String(missingCount)} of ${String(entries.length)} locales are missing`]
      : []),
  ];
  const pending = pendingLocales(drafts);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title={copy?.title ?? cluster.slug}
        description={`Cluster ${cluster.slug} · ${String(entries.length)} locales`}
        actions={
          <Button
            disabled={publishReasons.length > 0}
            onClick={() => {
              setPublishConfirm(true);
            }}
          >
            Publish
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-muted-foreground" {...LTR_ISOLATE_ATTRS}>
          {cluster.slug}
        </span>
        <Badge variant={cluster.status === "published" ? "success" : "outline"}>
          {cluster.status}
        </Badge>
        <Badge variant="outline">
          {String(entries.length - missingCount)} of {String(entries.length)} locales
        </Badge>
        {/* Q1 — the pair belongs to the selected locale, never to the cluster. */}
        <span className="text-sm text-muted-foreground" data-testid="locale-version-pair">
          {versions.served} · {versions.current}
        </span>
      </div>

      {publishReasons.length > 0 && (
        <p className="text-sm text-muted-foreground" data-testid="publish-reasons">
          <strong>Publish unavailable:</strong> {publishReasons.join("; ")}.
        </p>
      )}

      {pending.length > 0 && (
        <p className="text-sm text-warning-foreground" data-testid="unsaved-warning">
          {unsavedWarningCopy(pending)}
        </p>
      )}

      {blockers.length > 0 && (
        <div className="rounded-2xl border border-destructive p-4" data-testid="publish-blockers">
          <h4 className="text-sm font-semibold">The gate refused this publish</h4>
          <ul className="mt-2 grid gap-1 text-sm text-muted-foreground">
            {blockers.map((issue) => (
              <li key={`${issue.path}:${issue.code}`}>
                <span className="font-mono" {...LTR_ISOLATE_ATTRS}>
                  {issue.path}
                </span>{" "}
                — {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(errors.length > 0 || warnings.length > 0) && (
        <AdminSectionCard
          title="Validation"
          description={`${String(errors.length)} errors · ${String(warnings.length)} warnings`}
        >
          <div className="grid gap-4">
            {factGaps.length > 0 && (
              <div className="grid gap-2" data-testid="fact-value-gaps">
                <h4 className="text-sm font-semibold">
                  {String(factGaps.length)} fact values still to write
                </h4>
                <p className="max-w-prose text-sm text-muted-foreground">{FACT_GAP_GUIDANCE}</p>
                <ul className="grid gap-1 text-sm">
                  {factGaps.map(({ issue, locale: at }) => (
                    <li
                      key={`g:${issue.path}:${issue.code}`}
                      className="flex flex-wrap items-center gap-2"
                    >
                      <span className="font-mono text-muted-foreground" {...LTR_ISOLATE_ATTRS}>
                        {issue.path}
                      </span>
                      <span className="text-muted-foreground">— {issue.message}</span>
                      {at !== null && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            openLocaleEditor(at);
                          }}
                        >
                          Write it in {englishNameFor(at)}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {factDrifts.length > 0 && (
              <div className="grid gap-2" data-testid="fact-definition-drift">
                <h4 className="text-sm font-semibold text-destructive">
                  {String(factDrifts.length)} copies reference an undefined fact
                </h4>
                <p className="max-w-prose text-sm text-muted-foreground">{FACT_DRIFT_GUIDANCE}</p>
                <ul className="grid gap-1 text-sm">
                  {factDrifts.map(({ issue, locale: at }) => (
                    <li
                      key={`d:${issue.path}:${issue.code}`}
                      className="flex flex-wrap items-center gap-2 text-destructive"
                    >
                      <span className="font-mono" {...LTR_ISOLATE_ATTRS}>
                        {issue.path}
                      </span>
                      <span>— {issue.message}</span>
                      {at !== null && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            openLocaleEditor(at);
                          }}
                        >
                          Open {englishNameFor(at)}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(otherErrors.length > 0 || warnings.length > 0) && (
              <ul className="grid gap-1 text-sm" data-testid="validation-issues">
                {otherErrors.map((issue) => (
                  <li key={`e:${issue.path}:${issue.code}`} className="text-destructive">
                    <span className="font-mono" {...LTR_ISOLATE_ATTRS}>
                      {issue.path}
                    </span>{" "}
                    — {issue.message}
                  </li>
                ))}
                {warnings.map((issue) => (
                  <li key={`w:${issue.path}:${issue.code}`} className="text-muted-foreground">
                    <span className="font-mono" {...LTR_ISOLATE_ATTRS}>
                      {issue.path}
                    </span>{" "}
                    — {issue.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </AdminSectionCard>
      )}

      {restored !== null && (
        <RestoreBanner
          restoredVersion={restored.restoredVersion}
          sourceVersion={restored.sourceVersion}
          localeCount={entries.length}
          publishing={publishMutation.isPending}
          onPublish={() => {
            publishMutation.mutate();
          }}
          onDismiss={() => {
            setRestored(null);
          }}
        />
      )}

      <LocaleSelect
        id="blog-locale"
        entries={entries}
        value={locale}
        onChange={(next) => {
          setParam("locale", next);
        }}
        className="max-w-sm"
      />

      <Tabs
        value={mode}
        onValueChange={(next) => {
          setParam("mode", next);
        }}
      >
        <TabsList>
          <TabsTrigger value="view">Viewer</TabsTrigger>
          <TabsTrigger value="edit">Editor</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="media">Images</TabsTrigger>
        </TabsList>

        <TabsContent value="view">
          <ViewerFrame
            cluster={cluster}
            entry={entry}
            mode={frameMode}
            onModeChange={(next) => {
              setParam("frame", next);
            }}
            baseUrl={MARKETING_BASE_URL}
          />
        </TabsContent>

        <TabsContent value="edit">
          {copy === undefined ? (
            <p className="text-sm text-muted-foreground" data-testid="editor-no-copy">
              This locale has no copy to edit yet.
            </p>
          ) : (
            <div className="space-y-6">
              <LocaleEditorForm
                key={`${locale}:${String(formEpoch)}`}
                copy={copy}
                entry={entry}
                facts={cluster.facts}
                // Only this copy's issues, addressed as this copy's fields —
                // the verdict is cluster-wide and says `locales.de.…`, which
                // matches no control and belongs to nobody editing Finnish.
                issues={issuesForLocale(errors, locale)}
                saving={saveMutation.isPending}
                cachedValues={readDraft(drafts, locale)}
                onCacheValues={(values, dirty) => {
                  if (suppressFlush.current) {
                    suppressFlush.current = false;
                    return;
                  }
                  setDrafts((current) =>
                    dirty ? putDraft(current, locale, values) : clearDraft(current, locale),
                  );
                }}
                onSubmit={(submission) => {
                  saveMutation.mutate({ submission, baseVersion: copy.version });
                }}
              />
              <ClusterInvariantsPanel
                facts={cluster.facts}
                body={copy.body}
                localeCount={entries.length}
                saving={clusterMutation.isPending}
                onSubmit={(submission) => {
                  clusterMutation.mutate({ ...submission, clusterVersion: cluster.clusterVersion });
                }}
              />
            </div>
          )}
        </TabsContent>

        <TabsContent value="history">
          <div className="space-y-6">
            <RevisionList
              revisions={revisions}
              currentVersion={copy?.version ?? 0}
              publishedVersion={entry.publishedVersion ?? null}
              onRestore={(version) => {
                restoreMutation.mutate(version);
              }}
              onSelect={(version) => {
                setDiffFrom(version);
              }}
            />
            <RevisionDiff
              pair={diffPair}
              revisionCount={revisions.length}
              diffs={diffs}
              loading={diffFromQuery.isLoading}
            />
          </div>
        </TabsContent>

        <TabsContent value="media">
          {mediaQuery.data === undefined ? (
            <p className="text-sm text-muted-foreground" data-testid="media-loading">
              Loading images…
            </p>
          ) : (
            <MediaPanel
              slug={cluster.slug}
              locale={locale}
              state={mediaQuery.data.data}
              requiredScreenshots={copy === undefined ? [] : requiredVariantsFor(copy.body)}
              baseUrl={MARKETING_BASE_URL}
              uploading={uploadMutation.isPending}
              uploadFailure={uploadFailure}
              onUpload={(target, submission) => {
                uploadMutation.mutate({
                  target,
                  file: submission.file,
                  reason: submission.reason,
                  acceptDimensions: submission.acceptDimensions,
                });
              }}
            />
          )}
        </TabsContent>
      </Tabs>

      {conflict !== null && (
        <ConflictDialog
          open
          onOpenChange={() => {
            setConflict(null);
          }}
          pairs={conflict.pairs}
          head={conflict.conflict.head}
          onTakeTheirs={() => {
            // Their copy becomes what the form shows. Nothing of mine is
            // destroyed on the server — my values were never written, and their
            // revision is still the head it always was.
            suppressFlush.current = true;
            setDrafts((current) =>
              putDraft(
                current,
                locale,
                buildLocaleFormDefaults(conflict.conflict.head, cluster.facts),
              ),
            );
            setFormEpoch((epoch) => epoch + 1);
            setConflict(null);
            void invalidate();
          }}
          onKeepMine={() => {
            const head = conflict.conflict.head;
            const submission = conflict.submission;
            setConflict(null);
            // Resubmit over the version that beat us, having shown both first.
            // Their revision stays in the history; append-only means nothing is
            // lost either way.
            saveMutation.mutate({ submission, baseVersion: head.version });
          }}
        />
      )}

      <Dialog open={publishConfirm} onOpenChange={setPublishConfirm}>
        <DialogContent data-testid="publish-confirm">
          <DialogHeader>
            <DialogTitle>Publish every language copy?</DialogTitle>
            <DialogDescription>{publishConfirmCopy(entries.length)}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPublishConfirm(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setPublishConfirm(false);
                publishMutation.mutate();
              }}
            >
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
